import { useEffect, useMemo, useRef, useState } from 'react';

import _ from 'lodash';
import debounce from 'lodash/debounce';
import { GlobeIcon, Loader2, MapPinIcon } from 'lucide-react';

import {
  BaseOptionType,
  Select,
  type SelectRootProps,
} from 'components/Select';
import { cn } from 'utils/shadcn';

import { Flex } from './layout';
import { Typography } from './typography';

export type ValueType = any;

export interface DebounceSelectProps<ValueType> extends Omit<
  SelectRootProps,
  'children' | 'onOpenChange' | 'style'
> {
  fetchOptions: (search: string) => Promise<ValueType[]>;
  debounceTimeout?: number;
  notFoundContent?: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  style?: React.CSSProperties;
}

export function DebounceSelect<
  ValueType extends {
    key?: string;
    label: React.ReactNode;
    value: string | number;
  } = any,
>({
  fetchOptions,
  options: initOptions,
  debounceTimeout = 300,
  notFoundContent = <Typography variant='body-sm'>No results found</Typography>,
  ...props
}: DebounceSelectProps<ValueType>) {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ValueType[]>(
    initOptions as ValueType[]
  );
  const fetchRef = useRef(0);
  const fetchOptionsRef = useRef(fetchOptions);
  fetchOptionsRef.current = fetchOptions;

  const debounceFetcher = useMemo(() => {
    const loadOptions = (searchText: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);

      fetchOptionsRef.current(searchText).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          return;
        }

        setOptions(newOptions);
        setFetching(false);
      });
    };

    return debounce(loadOptions, debounceTimeout);
  }, [debounceTimeout]);

  useEffect(() => {
    if (initOptions) {
      setOptions(initOptions as ValueType[]);
    }
  }, [initOptions]);

  const loadingSpinner = <Loader2 className='h-4 w-4 animate-spin mr-2' />;

  // Preserve order from API - no sorting (returns 0 means no change in order)
  // Caller can override this default behavior by passing a custom filterSort function prop
  const preserveOrderSort = (
    _optionA: BaseOptionType,
    _optionB: BaseOptionType
  ) => 0;

  // Wrap onChange to include full option data (labelInValue only returns { label, value })
  const handleChange = (
    value: Parameters<NonNullable<typeof props.onChange>>[0],
    option?: Parameters<NonNullable<typeof props.onChange>>[1]
  ) => {
    if (!props.onChange) return;

    // If we have the option from Select, use it directly (it may have full data)
    // Otherwise fall back to looking up in our options state
    if (option && !Array.isArray(option) && 'externalTMSID' in option) {
      props.onChange(option, option);
      return;
    }

    // Look up full option from our options state by value
    if (value && typeof value === 'object' && 'value' in value && options) {
      const fullOption = options.find(
        (opt) => String(opt.value) === String(value.value)
      );
      if (fullOption) {
        props.onChange(fullOption, fullOption);
        return;
      }
    }

    // Fallback to original behavior
    props.onChange(value, option);
  };

  return (
    <Select
      showSearch
      labelInValue
      filterOption={false}
      filterSort={preserveOrderSort}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? loadingSpinner : notFoundContent}
      {...props}
      onChange={handleChange}
      options={options}
      className={cn(
        '[&_input]:bg-transparent!',
        // Disabled styling - matches Input component's readonly styling
        props.disabled && [
          'cursor-not-allowed!',
          '[&_.ant-select-selector]:bg-neutral-200!',
          '[&_.ant-select-selector]:border-neutral-400!',
          '[&_.ant-select-selector]:cursor-not-allowed!',
          '[&_.ant-select-selection-item]:text-neutral-500!',
          '[&_.ant-select-selection-search-input]:cursor-not-allowed!',
        ],
        props.className
      )}
    />
  );
}

// Using Inline CSS for this component since it gets appended outside our root element
export const GenericLocationOption = ({
  option,
  optionFieldsToRender = [],
}: {
  option: BaseOptionType;
  optionFieldsToRender?: string[]; // If empty, the default option fields rendered are ${`mainAddress`} - ${`secondaryAddress`}
}) => {
  const isOptionFieldsEmpty =
    optionFieldsToRender.map((op) => option?.[op]).filter(Boolean).length === 0;

  return (
    <Flex direction='col' gap='xs' className='w-full'>
      <Typography variant='body-sm' className='text-[13px]'>
        {option.name}
      </Typography>

      {!isOptionFieldsEmpty ? (
        <Flex gap='xs' align='start' className='whitespace-pre-wrap'>
          <MapPinIcon
            style={{
              height: '13px',
              width: '13px',
              flexShrink: '0',
              marginTop: '2px',
            }}
          />
          <Typography variant='body-xs'>
            {optionFieldsToRender && optionFieldsToRender.length > 0
              ? `${optionFieldsToRender
                  .map((field) => {
                    if (!option[field] || field === 'source') {
                      return '';
                    }
                    return option[field];
                  })
                  .filter(Boolean)
                  .join(', ')}`
              : `${option.mainAddress} — ${option.secondaryAddress}`}
          </Typography>
        </Flex>
      ) : null}

      {optionFieldsToRender.includes('source') && option.source ? (
        <Flex gap='xs' className='whitespace-pre-wrap'>
          <GlobeIcon
            style={{
              height: '13px',
              width: '13px',
              flexShrink: '0',
              marginTop: '2px',
            }}
          />
          <Typography variant='body-xs'>
            {_.startCase(_.toLower(option.source))}
          </Typography>
        </Flex>
      ) : null}
    </Flex>
  );
};
